import { Location } from "@remix-run/react";
import { appName, websiteDomain } from "./environment";

type PageHead = {
  title: string;
  description?: string;
  author?: string;
  appendAppName?: boolean;
  image?: string;
  type?: "website" | "article";
  location?: Location;
  keywords?: string[];
};

type HeadData = {
  title?: string;
  name?: string;
  content?: string;
}[];

function og(name: string, content: string) {
  return {
    property: `og:${name}`,
    content,
  };
}

function twitter(name: string, content: string) {
  return {
    name: `twitter:${name}`,
    content,
  };
}

export function pageHead(data: PageHead) {
  const headData: HeadData = [];

  if (data?.appendAppName) {
    data.title = `${data.title} | ${appName}`;
  }

  metaTitleGenerator(data.title, headData);

  const type = data.type || "website";

  og("site_name", websiteDomain || `${appName}.com`);
  og("type", type);

  if (data.location) {
    og(
      "url",
      `https://${websiteDomain}${data.location.pathname + data.location.search}`,
    );
  }

  if (data.author) {
    og("author", data.author);
    twitter("creator", data.author);
  }

  if (data.keywords && Array.isArray(data.keywords)) {
    headData.push({
      name: "keywords",
      content: data.keywords.join(", "), // Convert array to a comma-separated string
    });
  }

  metaDescriptionGenerator(data.description || "", headData);

  metaImageGenerator(data.image || "", headData);

  return headData;
}

export function metaDescriptionGenerator(
  description: string,
  headData: HeadData,
) {
  headData.push(
    // default description
    meta("description", description),
    // graph description
    meta("og:description", description),
    // twitter description
    meta("twitter:description", description),
  );
}

export function metaTitleGenerator(title: string, headData: HeadData) {
  // we to return an object containing the title and all of the meta title for graph, twitter, etc

  headData.push({ title });

  headData.push(
    // graph title
    meta("og:title", title),
    // twitter title
    meta("twitter:title", title),
  );
}

function meta(name: string, content: string) {
  return {
    name,
    content,
  };
}

function property(name: string, content: string) {
  return {
    property: name,
    content,
  };
}

export function metaImageGenerator(imagePath: string, headData: HeadData) {
  if (!imagePath) return;

  headData.push(
    // graph image
    property("og:image", imagePath),
    // twitter image
    property("twitter:image", imagePath),
  );
}
